import { colors, theme } from '@config/theme'
import { getISODay, parseISO } from 'date-fns'
import styled, { ThemeProvider } from 'styled-components'

const weekdayLabels = ['M', 'Tu', 'W', 'Th', 'F']
const weekdayTestIds = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']

export const DatesOneWeek = ({ dates }: { dates: string[] | null }) => {
  if (!dates || dates.length === 0) {
    return null
  }

  const parsedDates = dates.map((d) => parseISO(d)).sort((a, b) => a.getTime() - b.getTime())
  const activeWeekdays = parsedDates.map((date) => getISODay(date))

  const normalizeDate = (date: string) => {
    const dateObj = new Date(date)
    const day = dateObj.getUTCDate()
    const monthYear = dateObj.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })

    return { day, monthYear }
  }

  const sortedDates = dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
  const { day: startDay, monthYear: startMonthYear } = normalizeDate(sortedDates[0])
  const { day: endDay, monthYear: endMonthYear } = normalizeDate(sortedDates[sortedDates.length - 1])

  return (
    <ThemeProvider theme={theme}>
      <Container data-testid={'date-card'} data-status="1week">
        <DateContainer>
          <DateItem>
            <Month data-testid="date_module_1week_month_year_element_from">{startMonthYear}</Month>
            <Day data-testid="date_module_1week_day_element_from">{startDay}</Day>
          </DateItem>
          <Day style={{ marginBottom: '2px' }}>-</Day>
          <DateItem>
            <Month data-testid="date_module_1week_month_year_element_to">{endMonthYear}</Month>
            <Day data-testid="date_module_1week_day_element_to">{endDay}</Day>
          </DateItem>
        </DateContainer>
        <Weekdays>
          {weekdayLabels.map((label, i) => {
            const dayNum = i + 1
            const isActive = activeWeekdays.includes(dayNum)
            return (
              <DayLabel
                key={dayNum}
                isActive={isActive}
                data-testid={`date_module_1week_weekday_${weekdayTestIds[i]}`}
                data-weekday-status={isActive ? 'uncrossed' : 'crossed'}
              >
                {label}
                {!isActive && <CrossLine />}
              </DayLabel>
            )
          })}
        </Weekdays>
      </Container>
    </ThemeProvider>
  )
}

const Container = styled.div`
  max-width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`
const DateContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 4px;
`

// Styled Components
const DateItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media${({ theme }) => theme.device['md']} {
    width: 100%;
  }
`

const Month = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: ${colors.brandGray700};
  white-space: nowrap;
`

const Day = styled.div`
  font-size: 36px;
  font-weight: 600;
  line-height: 30px;
  color: ${colors.brandGray900};
`

const Weekdays = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 0.5rem;
`

const DayLabel = styled.div<{ isActive: boolean }>`
  position: relative;
  font-weight: 400;
  font-size: 1rem;
  width: 24px;
  text-align: center;
  color: ${({ isActive }) => (isActive ? colors.brandGray700 : colors.brandGray400)};
`

const CrossLine = styled.div`
  position: absolute;
  top: 6px;
  left: -7px;
  width: 100%;
  height: 100%;
  border-top: 1px solid ${colors.brandRed};
  transform: rotate(45deg);
  pointer-events: none;
`
